import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { isEntryEditable, validateEntryForRound, type Entry, type Fixture, type Round } from '@h2h/shared';
import { type ScorerOptions } from '../../components/play/SlatePickFlow';
import { useSubmitEntry } from '../../hooks/useQueries';
import { draftFromEntry, draftToWin, emptyCard, isCardStarted, toCards, type DraftCard } from '../../lib/picks';
import { maybePromptPushPermission } from '../../lib/push';
import { useHaptics } from '../../hooks/useHaptics';

export function useMakePicksDraft({
  leagueId,
  roundId,
  round,
  fixtures,
  entry,
  uid,
  now,
  scorerEnabled,
  squads,
  fixtureFocus
}: {
  leagueId: string | undefined;
  roundId: string | null;
  round: Round | null | undefined;
  fixtures: Fixture[] | undefined;
  entry: Entry | null | undefined;
  uid: string | null;
  now: number;
  scorerEnabled: boolean;
  squads: Record<string, { players: { id: string; name: string }[] }> | undefined;
  /** Deep-link straight into the card builder for this fixture. */
  fixtureFocus?: string | null;
}) {
  const navigate = useNavigate();
  const { impact } = useHaptics();
  const submit = useSubmitEntry(uid);
  const autosave = useSubmitEntry(uid);
  const lastSavedRef = useRef('');

  const fixtureById = useMemo(() => {
    const m = new Map<string, Fixture>();
    for (const f of fixtures ?? []) m.set(f.fixtureId, f);
    return m;
  }, [fixtures]);
  const sortedFixtures = useMemo(
    () => [...(fixtures ?? [])].sort((a, b) => a.kickoffAt - b.kickoffAt),
    [fixtures]
  );
  const legCatalogFor = (fixtureId: string): readonly string[] | undefined => fixtureById.get(fixtureId)?.legCatalog;

  const scorerFor = (f: Fixture): ScorerOptions | null => {
    if (!scorerEnabled || !squads) return null;
    const homePlayers = f.homeTeamId ? squads[f.homeTeamId]?.players ?? [] : [];
    const awayPlayers = f.awayTeamId ? squads[f.awayTeamId]?.players ?? [] : [];
    return homePlayers.length || awayPlayers.length ? { homePlayers, awayPlayers } : null;
  };

  const isKickoffLocked = (f: Fixture | undefined): boolean => !!f && (f.status !== 'SCHEDULED' || now >= f.kickoffAt);
  const openFixtures = useMemo(
    () => (fixtures ?? []).filter((f) => f.status === 'SCHEDULED' && now < f.kickoffAt),
    [fixtures, now]
  );

  const [cards, setCards] = useState<Record<string, DraftCard>>({});
  const [view, setView] = useState<'overview' | 'builder'>('overview');
  const [activeId, setActiveId] = useState<string | null>(null);
  const [seeded, setSeeded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const autoOpenedRef = useRef<string | null>(null);

  useLayoutEffect(() => {
    if (seeded || !round || fixtures == null || entry === undefined) return;
    const rec: Record<string, DraftCard> = {};
    if (entry) {
      for (const c of draftFromEntry(entry)) rec[c.fixtureId] = c;
    }
    const focus = fixtureFocus ?? null;
    const focusFixture = focus ? fixtureById.get(focus) : undefined;
    const openFocused =
      focus != null &&
      focusFixture != null &&
      !isKickoffLocked(focusFixture) &&
      autoOpenedRef.current !== focus;
    if (openFocused) {
      autoOpenedRef.current = focus;
      impact('light');
      if (!rec[focus]) rec[focus] = emptyCard(focus);
      setCards(rec);
      setActiveId(focus);
      setView('builder');
    } else if (entry) {
      setCards(rec);
    }
    setSeeded(true);
  }, [seeded, round, fixtures, entry, fixtureFocus, fixtureById, now, impact]);

  const cardByFixture = useMemo(() => {
    const m = new Map<string, DraftCard>();
    for (const c of Object.values(cards)) m.set(c.fixtureId, c);
    return m;
  }, [cards]);
  const startedCards = useMemo(() => Object.values(cards).filter(isCardStarted), [cards]);

  const pickedKickoffs = startedCards
    .map((c) => fixtureById.get(c.fixtureId)?.kickoffAt)
    .filter((k): k is number => k != null);
  const frozen = round != null && !isEntryEditable(round, pickedKickoffs, now);

  const predictions = useMemo(() => toCards(startedCards), [startedCards]);
  const valid =
    predictions != null &&
    predictions.length >= 1 &&
    round != null &&
    validateEntryForRound(predictions, round, legCatalogFor).ok;

  const toWinTotal = startedCards.reduce((sum, c) => sum + draftToWin(c), 0);

  const canStartNewEntry = round != null && isEntryEditable(round, [], now) && openFixtures.length >= 1;
  const newEntryClosed = entry === null && round != null && fixtures != null && !canStartNewEntry && startedCards.length === 0;
  const closed = round != null && round.status !== 'OPEN' && round.status !== 'LOCKING';

  const nextLockAt = openFixtures.map((f) => f.kickoffAt).sort((a, b) => a - b)[0] ?? null;

  const openBuilder = (fixtureId: string) => {
    const f = fixtureById.get(fixtureId);
    if (!f || isKickoffLocked(f)) return;
    impact('light');
    setCards((cur) => (cur[fixtureId] ? cur : { ...cur, [fixtureId]: emptyCard(fixtureId) }));
    setActiveId(fixtureId);
    setView('builder');
  };
  const updateCard = (next: DraftCard) => {
    if (frozen || isKickoffLocked(fixtureById.get(next.fixtureId))) return;
    setCards((cur) => ({ ...cur, [next.fixtureId]: next }));
  };
  const closeBuilder = () => {
    setCards((cur) => {
      const c = activeId ? cur[activeId] : undefined;
      if (activeId && c && !isCardStarted(c)) {
        const next = { ...cur };
        delete next[activeId];
        return next;
      }
      return cur;
    });
    setActiveId(null);
    setView('overview');
  };

  useEffect(() => {
    if (!leagueId || !roundId || !round || !valid || !predictions || frozen) return;
    const sig = JSON.stringify(predictions);
    if (sig === lastSavedRef.current) return;
    const id = window.setTimeout(() => {
      lastSavedRef.current = sig;
      autosave.mutate(
        { leagueId, roundId, predictions },
        { onError: () => { if (lastSavedRef.current === sig) lastSavedRef.current = ''; } }
      );
    }, 500);
    return () => window.clearTimeout(id);
  }, [predictions, valid, frozen, leagueId, roundId, round, autosave]);

  async function onCommit() {
    if (!valid || !predictions || !leagueId || !roundId) return;
    setError(null);
    impact('medium');
    try {
      await submit.mutateAsync({ leagueId, roundId, predictions });
      lastSavedRef.current = JSON.stringify(predictions);
      void maybePromptPushPermission();
      navigate(`/play/${leagueId}/waiting`);
    } catch (err) {
      setError((err as { message?: string })?.message ?? 'Could not save your cards. Please try again.');
    }
  }

  const activeFixture = activeId ? fixtureById.get(activeId) : undefined;
  const activeCard = activeId ? cards[activeId] ?? emptyCard(activeId) : null;
  const activeFrozen = frozen || isKickoffLocked(activeFixture);
  const inBuilder = view === 'builder' && !!activeFixture && !!activeCard;

  return {
    seeded,
    error,
    closed,
    newEntryClosed,
    frozen,
    valid,
    entry,
    sortedFixtures,
    cardByFixture,
    startedCards,
    toWinTotal,
    nextLockAt,
    legCatalogFor,
    scorerFor,
    openBuilder,
    updateCard,
    closeBuilder,
    onCommit,
    inBuilder,
    activeFixture,
    activeCard,
    activeFrozen,
    submit,
    autosave
  };
}
